/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Accordion, Table, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { useAlerts } from '../../context';

import { formatDateAndTime, capitalize, handleResponseError } from '../../../utils';

import { REQUEST_RETURN_PENDING_REVIEW, TRANS_QUEUED, TRANS_FAILED } from '../../../constants';

import AccordionItem from '../AccordionItem';
import RequestForReturnModal from './RequestForReturnModal';

function TxnRow({ label, val }) {
  const { t } = useTranslation();

  let value = val;
  if (!val) value = <span className="text-info">{t('common:labels.na')}</span>;
  return (
    <tr>
      <td>{label}</td>
      <td>{value}</td>
    </tr>
  );
}

const renderAccountInfo = (type, value, customer) => {
  const { t } = useTranslation();

  return (
    <>
      <TxnRow label={t('common:transactions.labels.entityName')} val={value.entity_name} />
      <TxnRow
        label={t('common:labels.userHandle')}
        val={
          <Button
            href={`/${customer ? 'settings' : 'admin'}/end_user/${value.user_handle}/profile`}
            variant="link"
            className="p-0 text-reset text-underline"
          >
            {value.user_handle}
          </Button>
        }
      />
      <TxnRow
        label={t('common:transactions.labels.nickname')}
        val={value.nickname || t('common:transactions.labels.default')}
      />

      <TxnRow
        label={t('common:transactions.labels.paymentInstrumentUUID')}
        val={
          <Button
            href={`/${customer ? 'settings' : 'admin'}/end_user/${
              value.user_handle
            }/payment-instruments/${value.gla_uuid}`}
            variant="link"
            className="p-0 text-reset text-underline"
          >
            {value.gla_uuid}
          </Button>
        }
      />

      {value.payment_instrument_type === 'bank_account' && (
        <>
          <TxnRow label={t('common:transactions.labels.routing')} val={value.routing_number} />
          <TxnRow
            label={t('common:transactions.labels.account')}
            val={value.last_4 ? `${value.last_4}*` : null}
          />
          <TxnRow
            label={t('common:transactions.labels.linkStatus')}
            val={value.link_status || null}
          />
        </>
      )}
      {value.payment_instrument_type === 'blockchain_address' && (
        <>
          <TxnRow label={t('applications.detail.form.fields.address.label')} val={value.address} />
          <TxnRow
            label={t('common:transactions.labels.wallets.type.label')}
            val={t(
              `common:transactions.labels.wallets.type.${value.blockchain_network.toLowerCase()}`
            )}
          />
          <TxnRow
            label={t('common:transactions.labels.wallets.migration.label')}
            val={value.migrated_at || null}
          />
        </>
      )}
      {value.payment_instrument_type === 'card' && (
        <>
          <TxnRow label={t('common:transactions.labels.cards.network')} val={value.card_network} />
          <TxnRow label={t('common:transactions.labels.cards.type')} val={value.card_type} />
          <TxnRow label={t('common:transactions.labels.cards.number')} val={`*${value.last_4}`} />
        </>
      )}
      <TxnRow label={t('common:transactions.labels.frozen')} val={capitalize(value.frozen)} last />
    </>
  );
};

function TransactionForm({ customer = false, admin = false, initialValues, user }) {
  const { t } = useTranslation();
  const { setAlert } = useAlerts();
  const [expanded, setExpanded] = useState([1]);
  const [requestReturn, setRequestReturn] = useState({
    show: false,
    percentage: {},
    return_code: initialValues.return_code || undefined,
    uuid: initialValues.transaction_return_request_uuid || undefined,
  });

  const handleExpanded = (key) =>
    setExpanded(expanded.includes(key) ? expanded.filter((e) => e !== key) : [...expanded, key]);
  const accordionActiveKey = (key) => expanded.includes(key) && key;

  const submitRequestReturn = (values) => {
    if (values) {
      const { name } = values.files[0];
      const formData = new FormData();
      formData.append('file', values.files[0]);
      formData.append(
        'data',
        JSON.stringify({
          name,
          filename: name,
          mime_type: values.files[0].type,
          support_document: {
            filename: name,
            mime_type: values.files[0].type,
          },
          return_code: values.return_code,
          customer_contact_date: values.customer_contact_date,
          team_id: user && user.currentTeam.id ? user.currentTeam.id : undefined,
          transaction_uuid:
            initialValues && initialValues.transaction_uuid
              ? initialValues.transaction_uuid
              : undefined,
          return_request_status: REQUEST_RETURN_PENDING_REVIEW,
        })
      );
      const xhr = new XMLHttpRequest();
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentage = Math.floor((event.loaded / event.total) * 100);
          setRequestReturn({
            ...requestReturn,
            percentage: { ...requestReturn.percentage, [name]: percentage },
          });
        }
      };
      xhr.onreadystatechange = () => {
        if (xhr.readyState !== 4) return;
        const json = JSON.parse(xhr.responseText);
        setAlert({
          message:
            xhr.status === 200
              ? t('common:transactions.detail.requestReturn.messages.success')
              : handleResponseError(json),
          style: xhr.status === 200 ? 'success' : 'danger',
        });
        delete requestReturn.percentage[name];
        setRequestReturn({
          show: false,
          percentage: requestReturn.percentage,
          return_code:
            xhr.status === 200 ? REQUEST_RETURN_PENDING_REVIEW : requestReturn.return_code,
          uuid: xhr.status === 200 ? json.transaction_return_request_uuid : requestReturn.uuid,
        });
      };
      xhr.open('POST', '/api/v2/request_reverse_virtual_account_ach_transaction', true);
      xhr.send(formData);
    } else {
      setRequestReturn({ show: false, percentage: {} });
    }
  };

  return (
    <>
      {/* Critical Information Section */}
      <Accordion id="critical" className="mb-4" activeKey={accordionActiveKey(1)}>
        <AccordionItem
          className="border rounded"
          eventKey={1}
          label={t('common:transactions.detail.headers.critical')}
          expanded={expanded}
          onSetExpanded={handleExpanded}
        >
          <div className="m-n4">
            <Table responsive className="custom-detail twoCol">
              <thead>
                <tr>
                  <th>{t('common:labels.detail')}</th>
                  <th>{t('common:labels.data')}</th>
                </tr>
              </thead>
              <tbody>
                <TxnRow
                  label={t('common:labels.created')}
                  val={formatDateAndTime(initialValues.created)}
                />
                <TxnRow
                  label={t('common:labels.userHandle')}
                  val={
                    <span>
                      <Link
                        to={`/${customer ? 'settings' : 'admin'}/end_user/${
                          initialValues.source.user_handle
                        }/profile`}
                        className="p-0 text-reset text-underline"
                      >
                        {initialValues.source.user_handle}
                      </Link>
                    </span>
                  }
                />
                <TxnRow
                  label={t('common:applications.handle')}
                  val={
                    customer ? (
                      initialValues.app.app_handle
                    ) : (
                      <Link
                        to={`/admin/app/${initialValues.app.app_id}/detail`}
                        className="p-0 text-reset text-underline"
                      >
                        {initialValues.app.app_handle}
                      </Link>
                    )
                  }
                />
                <TxnRow
                  label={t('common:applications.nickname')}
                  val={
                    customer ? (
                      initialValues.app.app_handle
                    ) : (
                      <Link
                        to={`/admin/app/${initialValues.app.app_id}/detail`}
                        className="p-0 text-reset text-underline"
                      >
                        {initialValues.app.app_nickname}
                      </Link>
                    )
                  }
                />
                <TxnRow
                  label={t('common:transactions.labels.source')}
                  val={
                    <Link
                      to={`/${customer ? 'settings' : 'admin'}/end_user/${
                        initialValues.source.user_handle
                      }/payment-instruments/${initialValues.source.gla_uuid}`}
                      className="p-0 text-reset text-underline"
                    >
                      {initialValues.source.payment_instrument_type === 'bank_account'
                        ? t('common:transactions.labels.bank_account_number', {
                            number: initialValues.source.last_4,
                          })
                        : initialValues.source.payment_instrument_type === 'card'
                        ? t('common:transactions.labels.bank_account_number', {
                            number: initialValues.source.last_4,
                          })
                        : t('common:transactions.labels.blockchain_address_nickname', {
                            nickname:
                              initialValues.source.nickname ||
                              t('common:transactions.labels.default'),
                            app: initialValues.app.app_nickname,
                          })}
                    </Link>
                  }
                />
                <TxnRow
                  label={t('common:transactions.labels.destination')}
                  val={
                    <Link
                      to={`/${customer ? 'settings' : 'admin'}/end_user/${
                        initialValues.destination.user_handle
                      }/payment-instruments/${initialValues.destination.gla_uuid}`}
                      className="p-0 text-reset text-underline"
                    >
                      {initialValues.destination.payment_instrument_type === 'bank_account'
                        ? t('common:transactions.labels.bank_account_number', {
                            number: initialValues.destination.last_4,
                          })
                        : initialValues.destination.payment_instrument_type === 'card'
                        ? t('common:transactions.labels.bank_account_number', {
                            number: initialValues.destination.last_4,
                          })
                        : t('common:transactions.labels.blockchain_address_nickname', {
                            nickname:
                              initialValues.destination.nickname ||
                              t('common:transactions.labels.default'),
                            app: initialValues.app.app_nickname,
                          })}
                    </Link>
                  }
                />
                <TxnRow
                  label={t('common:transactions.labels.transactionType')}
                  val={t(`common:transactions.${initialValues.transaction_type}`)}
                />
                <TxnRow
                  label={t('common:transactions.labels.processingType')}
                  val={initialValues.processing_type}
                />
                {initialValues.ach_details.is_debit &&
                  initialValues.transaction_type !== 'transfer' && (
                    <TxnRow
                      label={t('common:transactions.labels.debit')}
                      val={capitalize(initialValues.ach_details.is_debit)}
                    />
                  )}
              </tbody>
            </Table>
          </div>
        </AccordionItem>
      </Accordion>

      {/* Transaction Details Section */}
      <Accordion id="transaction" className="mb-4" activeKey={accordionActiveKey(2)}>
        <AccordionItem
          className="border rounded"
          eventKey={2}
          label={t('common:transactions.detail.headers.transaction')}
          expanded={expanded}
          onSetExpanded={handleExpanded}
        >
          <div className="m-n4">
            <Table responsive className="custom-detail twoCol">
              <thead>
                <tr>
                  <th>{t('common:labels.detail')}</th>
                  <th>{t('common:labels.data')}</th>
                </tr>
              </thead>
              <tbody>
                {!customer && (
                  <TxnRow
                    label={t('common:transactions.labels.id')}
                    val={initialValues.transaction_db_id}
                  />
                )}

                <TxnRow
                  label={t('common:transactions.labels.uuid')}
                  val={initialValues.transaction_uuid}
                />
                <TxnRow
                  label={t('common:transactions.labels.addenda')}
                  val={initialValues.ach_details.addenda_uuid || null}
                />
                <TxnRow
                  label={t('common:transactions.labels.status')}
                  val={
                    <span
                      className={`text-${
                        initialValues.transaction_status === 'failed'
                          ? 'warning'
                          : initialValues.transaction_status === 'success'
                          ? 'success'
                          : 'primary'
                      }`}
                    >
                      {capitalize(initialValues.transaction_status)}
                    </span>
                  }
                />
                <TxnRow
                  last
                  label={t('common:transactions.labels.usdStatus')}
                  val={
                    <span
                      className={`text-${
                        initialValues.transaction_status_details.usd_status === 'failed'
                          ? 'warning'
                          : initialValues.transaction_status_details.usd_status === 'success'
                          ? 'success'
                          : 'primary'
                      }`}
                    >
                      {capitalize(initialValues.transaction_status_details.usd_status)}
                    </span>
                  }
                />
              </tbody>
            </Table>
          </div>
        </AccordionItem>
      </Accordion>

      {/* ACH Details Section */}
      <Accordion id="ach" className="mb-4" activeKey={accordionActiveKey(3)}>
        <AccordionItem
          className="border rounded"
          eventKey={3}
          label={t('common:transactions.detail.headers.ach')}
          expanded={expanded}
          onSetExpanded={handleExpanded}
        >
          <div className="m-n4">
            <Table responsive className="custom-detail twoCol">
              <thead>
                <tr>
                  <th>{t('common:labels.detail')}</th>
                  <th>{t('common:labels.data')}</th>
                </tr>
              </thead>
              <tbody>
                <TxnRow
                  label={t('common:labels.submitted')}
                  val={
                    initialValues.ach_details.submitted
                      ? formatDateAndTime(initialValues.ach_details.submitted)
                      : null
                  }
                />
                <TxnRow
                  label={t('common:transactions.labels.ach')}
                  val={initialValues.ach_details.ach_filename || null}
                />
                <TxnRow
                  label={t('common:transactions.labels.returnCode')}
                  val={initialValues.ach_details.return_code || null}
                />
                <TxnRow
                  label={t('common:transactions.labels.returnDescription')}
                  val={initialValues.ach_details.return_description || null}
                />
                <TxnRow
                  label={t('common:transactions.labels.processingType')}
                  val={initialValues.processing_type}
                />
                <TxnRow
                  label={t('common:transactions.labels.review')}
                  val={capitalize(initialValues.review_needed)}
                />
                <TxnRow
                  last
                  label={t('common:transactions.labels.traceNumber')}
                  val={initialValues.ach_details.trace_number || null}
                />
              </tbody>
            </Table>
          </div>
        </AccordionItem>
      </Accordion>

      {/* Source Account Section */}
      <Accordion id="source" className="mb-4" activeKey={accordionActiveKey(4)}>
        <AccordionItem
          className="border rounded"
          eventKey={4}
          label={`${t('common:transactions.detail.headers.source')} (${t(
            `common:transactions.labels.${initialValues.source.payment_instrument_type}`
          )})`}
          expanded={expanded}
          onSetExpanded={handleExpanded}
        >
          <div className="m-n4">
            <Table responsive className="custom-detail twoCol">
              <thead>
                <tr>
                  <th>{t('common:labels.detail')}</th>
                  <th>{t('common:labels.data')}</th>
                </tr>
              </thead>
              <tbody>{renderAccountInfo('source', initialValues.source, customer, admin)}</tbody>
            </Table>
          </div>
        </AccordionItem>
      </Accordion>

      {/* Destination Account Section */}
      <Accordion id="destination" activeKey={accordionActiveKey(5)}>
        <AccordionItem
          className="border rounded"
          eventKey={5}
          label={`${t('common:transactions.detail.headers.destination')} (${t(
            `common:transactions.labels.${initialValues.destination.payment_instrument_type}`
          )})`}
          expanded={expanded}
          onSetExpanded={handleExpanded}
        >
          <div className="m-n4">
            <Table responsive className="custom-detail twoCol">
              <thead>
                <tr>
                  <th>{t('common:labels.detail')}</th>
                  <th>{t('common:labels.data')}</th>
                </tr>
              </thead>
              <tbody>
                {renderAccountInfo('destination', initialValues.destination, customer, admin)}
              </tbody>
            </Table>
          </div>
        </AccordionItem>
      </Accordion>

      {/* Request Return Action Section */}
      {customer &&
        user &&
        (user.role === 1 || user.role === 2) &&
        ![TRANS_QUEUED, TRANS_FAILED].includes(initialValues.transaction_status) &&
        (initialValues.destination.payment_instrument_type === 'virtual_account' ||
          initialValues.source.payment_instrument_type === 'virtual_account') &&
        (initialValues.destination.user_handle === 'virtual_accounts_external_originator' ||
          initialValues.source.user_handle === 'virtual_accounts_external_originator') && (
          <p className="text-end mt-5">
            {!requestReturn.uuid && (
              <Button
                variant="primary"
                type="button"
                onClick={() => setRequestReturn({ ...requestReturn, show: true })}
              >
                {t('common:transactions.detail.buttons.requestReturn')}
              </Button>
            )}
            {requestReturn.uuid && (
              <Button
                href={`/settings/request-return/${requestReturn.uuid}/detail`}
                variant="primary"
                type="button"
              >
                {t('common:transactions.detail.buttons.viewRequestReturn')}
              </Button>
            )}
          </p>
        )}

      {/* Request Return Modal */}
      {requestReturn.show && (
        <RequestForReturnModal
          show={requestReturn.show}
          percentage={requestReturn.percentage}
          onHide={submitRequestReturn}
        />
      )}
    </>
  );
}

TransactionForm.propTypes = {
  /**
   * The customer toggle to determine if the form is used for customer transactions
   */
  customer: PropTypes.bool,
  /**
   * The admin toggle to determine if the form is used for the admin console
   */
  admin: PropTypes.bool,
  /**
   * The values of the form
   */
  // eslint-disable-next-line react/forbid-prop-types
  initialValues: PropTypes.object.isRequired,
};

export default TransactionForm;
